import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { XCircle, Loader2 } from "@/components/icons";
import api, { formatErr } from "../lib/api";

import Shell from "./appointment-confirm/Shell";
import AppointmentDetails from "./appointment-confirm/AppointmentDetails";
import RescheduleForm from "./appointment-confirm/RescheduleForm";
import PendingActions from "./appointment-confirm/PendingActions";
import ResultState from "./appointment-confirm/ResultState";

/**
 * Public appointment-confirmation landing page reached via the email link.
 * Orchestrates loading, action wiring and rendering of the three possible
 * states (pending actions, reschedule form, finalized state).
 */
export default function AppointmentConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const initialAction = (params.get("action") || "").toLowerCase();
  const noToken = !token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!noToken);
  const [loadErr, setLoadErr] = useState("");
  const [acting, setActing] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [reschedulingMode, setReschedulingMode] = useState(initialAction === "reschedule");
  const [newDate, setNewDate] = useState("");
  const [rescheduleErr, setRescheduleErr] = useState("");
  const [busySlots, setBusySlots] = useState([]);
  const [busyLoading, setBusyLoading] = useState(false);

  // Minimum picker datetime (1 hour from page load). Stable across renders.
  const [minDateTime] = useState(
    () => new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)
  );

  const fetchAppt = async () => {
    try {
      const r = await api.get(
        `/appointments/public/by-token?token=${encodeURIComponent(token)}`
      );
      setData(r.data);
      setLoadErr("");
    } catch (e) {
      setLoadErr(
        formatErr(e.response?.data?.detail) ||
          "El enlace no es válido o ya no está disponible."
      );
    }
  };

  const respond = async (action) => {
    setActing(true);
    try {
      const r = await api.post("/appointments/public/respond", { token, action });
      setActionResult({ state: r.data.state, error: null });
      fetchAppt();
    } catch (e) {
      setActionResult({
        state: null,
        error:
          formatErr(e.response?.data?.detail) ||
          "No pudimos registrar tu respuesta.",
      });
    } finally {
      setActing(false);
    }
  };

  const submitReschedule = async () => {
    setRescheduleErr("");
    if (!newDate) {
      setRescheduleErr("Selecciona una fecha y hora.");
      return;
    }
    setActing(true);
    try {
      await api.post("/appointments/public/reschedule", { token, new_date: newDate });
      setReschedulingMode(false);
      setActionResult({ state: "rescheduled", error: null });
      fetchAppt();
    } catch (e) {
      setRescheduleErr(
        formatErr(e.response?.data?.detail) || "No pudimos reprogramar la cita."
      );
    } finally {
      setActing(false);
    }
  };

  // Initial appointment load
  useEffect(() => {
    if (noToken) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(
          `/appointments/public/by-token?token=${encodeURIComponent(token)}`
        );
        if (!cancelled) {
          setData(r.data);
          setLoadErr("");
        }
      } catch (e) {
        if (!cancelled) {
          setLoadErr(
            formatErr(e.response?.data?.detail) ||
              "El enlace no es válido o ya no está disponible."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, noToken]);

  // Load busy slots when entering reschedule mode
  useEffect(() => {
    if (!reschedulingMode || !token) return;
    let cancelled = false;
    (async () => {
      setBusyLoading(true);
      try {
        const r = await api.get(
          `/appointments/public/busy-slots?token=${encodeURIComponent(token)}`
        );
        if (!cancelled) setBusySlots(r.data?.busy || []);
      } catch {
        if (!cancelled) setBusySlots([]);
      } finally {
        if (!cancelled) setBusyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reschedulingMode, token]);

  // Auto-fire the action if the email link included ?action=confirm|decline
  useEffect(() => {
    if (!data || !initialAction || actionResult) return;
    if (data.appointment.patient_confirmation !== "pending") return;
    if (!["confirm", "decline"].includes(initialAction)) return;
    const t = setTimeout(() => respond(initialAction), 0);
    return () => clearTimeout(t);
  }, [data]);

  // ---- render --------------------------------------------------------------

  if (noToken) {
    return (
      <Shell>
        <ErrorState message="Falta el token de confirmación." />
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell>
        <div className="text-center">
          <Loader2 className="w-7 h-7 text-black mx-auto animate-spin" />
          <p className="mt-4 text-sm text-black/55">Cargando tu cita…</p>
        </div>
      </Shell>
    );
  }

  if (loadErr) {
    return (
      <Shell>
        <ErrorState message={loadErr} testId="appt-confirm-error" />
      </Shell>
    );
  }

  const a = data.appointment;
  const state = a.patient_confirmation || "pending";
  const canReschedule = state !== "declined" && a.status !== "cancelled";

  return (
    <Shell wide>
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black text-white text-[11px] font-extrabold uppercase tracking-widest">
          ConsuDoctor
        </div>
        <h1
          className="mt-4 text-3xl font-extrabold text-black tracking-tight"
          data-testid="appt-confirm-title"
        >
          Tu cita médica
        </h1>
        <p className="mt-1 text-sm text-black/55">
          Revisa los detalles y confírmanos si podrás asistir.
        </p>
      </div>

      <AppointmentDetails data={data} />

      {reschedulingMode && canReschedule ? (
        <RescheduleForm
          appointment={a}
          busySlots={busySlots}
          busyLoading={busyLoading}
          newDate={newDate}
          setNewDate={setNewDate}
          minDateTime={minDateTime}
          rescheduleErr={rescheduleErr}
          acting={acting}
          onSubmit={submitReschedule}
          onCancel={() => {
            setReschedulingMode(false);
            setRescheduleErr("");
          }}
        />
      ) : state === "pending" ? (
        <PendingActions
          acting={acting}
          onConfirm={() => respond("confirm")}
          onReschedule={() => setReschedulingMode(true)}
          onDecline={() => respond("decline")}
          actionResult={actionResult}
        />
      ) : (
        <ResultState state={state} />
      )}
    </Shell>
  );
}

function ErrorState({ message, testId }) {
  return (
    <div className="text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-red-100 border border-red-200 flex items-center justify-center">
        <XCircle className="w-7 h-7 text-red-700" />
      </div>
      <h1 className="mt-5 text-2xl font-extrabold text-black tracking-tight">
        Enlace no válido
      </h1>
      <p className="mt-2 text-sm text-black/65" data-testid={testId}>
        {message}
      </p>
    </div>
  );
}
